import React, { useState, useEffect } from 'react';

const API = 'https://calendar.nareea.co.uk';

const STATUS = {
  on_track: { bg: '#e6f4ea', text: '#137333', label: 'On track', bar: '#34a853' },
  at_risk:  { bg: '#fef7e0', text: '#b06000', label: 'At risk',  bar: '#fbbc04' },
  behind:   { bg: '#fce8e6', text: '#c5221f', label: 'Behind',   bar: '#ea4335' },
};

// Format large numbers: 198000000 → $198M, 4200000 → 4.2M, 68000 → 68K
function fmt(value, unit) {
  if (typeof value === 'string') return value;
  if (unit === '$' || unit === 'ARR') {
    if (value >= 1e9) return `$${(value / 1e9).toFixed(1)}B`;
    if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
    if (value >= 1e3) return `$${(value / 1e3).toFixed(0)}K`;
    return `$${value}`;
  }
  if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(0)}K`;
  return `${value}`;
}

function fmtTarget(value, unit) {
  if (typeof value === 'string') return value;
  if (unit === '$' || unit === 'ARR') {
    if (value >= 1e6) return `$${(value / 1e6).toFixed(0)}M`;
    return `$${value}`;
  }
  if (value >= 1e6) return `${(value / 1e6).toFixed(1)}M`;
  if (value >= 1e3) return `${(value / 1e3).toFixed(0)}K`;
  return `${value}`;
}

function Badge({ status }) {
  const s = STATUS[status] || STATUS.at_risk;
  return (
    <span style={{ background: s.bg, color: s.text, fontSize: 10, fontWeight: 700,
      padding: '2px 9px', borderRadius: 10, whiteSpace: 'nowrap', letterSpacing: 0.3 }}>
      {s.label}
    </span>
  );
}

function Bar({ value, target, status }) {
  const pct = Math.min(100, Math.round((value / target) * 100));
  const color = STATUS[status]?.bar || '#fbbc04';
  return (
    <div style={{ marginTop: 5 }}>
      <div style={{ height: 4, background: '#e8eaed', borderRadius: 2 }}>
        <div style={{ height: '100%', width: `${pct}%`, background: color,
          borderRadius: 2, transition: 'width 0.8s ease' }} />
      </div>
      <div style={{ fontSize: 10, color: '#80868b', marginTop: 2 }}>{pct}% of target</div>
    </div>
  );
}

function OrgPanel({ org }) {
  const ns = org.north_star;
  return (
    <div style={{
      background: 'linear-gradient(135deg, #f8f9ff 0%, #f1f3ff 100%)',
      border: '1px solid #dadce0', borderRadius: 12, padding: '20px 28px',
      marginBottom: 20, display: 'grid',
      gridTemplateColumns: '1fr 1fr', gap: 24, alignItems: 'start'
    }}>
      {/* Left — North Star */}
      <div>
        <div style={{ fontSize: 10, color: '#80868b', fontWeight: 700,
          letterSpacing: 1.2, textTransform: 'uppercase', marginBottom: 6 }}>
          Organization North Star · {org.quarter}
        </div>
        <div style={{ fontSize: 20, fontWeight: 500, color: '#202124', marginBottom: 10 }}>
          {ns.metric}
        </div>
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
          <span style={{ fontSize: 38, fontWeight: 400, color: '#202124', letterSpacing: -1 }}>
            {fmt(ns.value, ns.unit)}
          </span>
          <span style={{ fontSize: 14, color: '#137333', fontWeight: 600 }}>{ns.trend}</span>
          <span style={{ fontSize: 13, color: '#80868b' }}>vs {fmtTarget(ns.target, ns.unit)} target</span>
        </div>
        <Bar value={ns.value} target={ns.target} status={ns.status} />
      </div>

      {/* Right — Secondary metrics 2x2 */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
        {org.secondary.map((s, i) => (
          <div key={i} style={{ background: '#fff', border: '1px solid #e8eaed',
            borderRadius: 8, padding: '10px 14px' }}>
            <div style={{ fontSize: 11, color: '#80868b', marginBottom: 3 }}>{s.metric}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
              <span style={{ fontSize: 17, fontWeight: 500, color: '#202124' }}>{s.value}</span>
              <span style={{ fontSize: 11, color: '#137333', fontWeight: 600 }}>{s.trend}</span>
            </div>
            <Badge status={s.status} />
          </div>
        ))}
      </div>
    </div>
  );
}

function TeamCard({ team, expanded, onToggle }) {
  const ns = team.north_star;
  return (
    <div style={{ border: '1px solid #e8eaed', borderRadius: 10,
      overflow: 'hidden', background: '#fff', height: 'fit-content' }}>

      {/* Header — always visible */}
      <div onClick={onToggle} style={{
        padding: '14px 16px', cursor: 'pointer',
        background: expanded ? '#f8f9fa' : '#fff',
        borderBottom: expanded ? '1px solid #e8eaed' : 'none'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 6 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontWeight: 600, fontSize: 13, color: '#202124' }}>{team.name}</span>
            <Badge status={ns.status} />
          </div>
          <span style={{ fontSize: 14, color: '#80868b',
            transform: expanded ? 'rotate(90deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s', display: 'inline-block' }}>›</span>
        </div>
        <div style={{ fontSize: 12, color: '#5f6368', marginBottom: 4 }}>
          {ns.metric}:&nbsp;
          <strong style={{ color: '#202124' }}>{fmt(ns.value, ns.unit)}</strong>
          <span style={{ color: '#80868b' }}> / {fmtTarget(ns.target, ns.unit)}</span>
          <span style={{ marginLeft: 8, color: ns.trend.startsWith('+') ? '#137333' : '#c5221f',
            fontWeight: 600 }}>{ns.trend}</span>
        </div>
        <Bar value={ns.value} target={ns.target} status={ns.status} />
      </div>

      {/* Expanded KPIs */}
      {expanded && (
        <div style={{ padding: '10px 16px 14px' }}>
          <div style={{ fontSize: 10, color: '#80868b', fontWeight: 700,
            letterSpacing: 0.8, textTransform: 'uppercase', marginBottom: 8 }}>Team KPIs</div>
          {team.kpis.map((kpi, i) => (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '7px 0',
              borderBottom: i < team.kpis.length - 1 ? '1px solid #f1f3f4' : 'none'
            }}>
              <span style={{ fontSize: 12, color: '#3c4043', flex: 1 }}>{kpi.metric}</span>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: '#202124' }}>{kpi.value}</span>
                <span style={{ fontSize: 11, color: '#80868b' }}>/ {kpi.target}</span>
                <span style={{ fontSize: 11, fontWeight: 600,
                  color: kpi.trend.startsWith('+') ? '#137333' : '#ea4335' }}>{kpi.trend}</span>
                <Badge status={kpi.status} />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const NorthStarPanel = () => {
  const [data, setData]               = useState(null);
  const [expanded, setExpanded]       = useState(null);
  const [filter, setFilter]           = useState('all');
  const [lastUpdated, setLastUpdated] = useState(new Date());

  useEffect(() => {
    const load = () => {
      fetch(`${API}/api/kpi`)
        .then(r => r.json())
        .then(d => { setData(d); setLastUpdated(new Date()); })
        .catch(() => fetch('/kpi-mock.json').then(r => r.json()).then(setData).catch(console.error));
    };
    load();
    const t = setInterval(() => { load(); }, 60000);
    return () => clearInterval(t);
  }, []);

  if (!data) return (
    <div style={{ padding: 60, textAlign: 'center', color: '#80868b', fontSize: 14 }}>
      Loading North Star Metrics…
    </div>
  );

  const filtered = filter === 'all' ? data.teams : data.teams.filter(t => t.north_star.status === filter);
  const counts = {
    on_track: data.teams.filter(t => t.north_star.status === 'on_track').length,
    at_risk:  data.teams.filter(t => t.north_star.status === 'at_risk').length,
    behind:   data.teams.filter(t => t.north_star.status === 'behind').length,
  };

  return (
    <div style={{
      maxWidth: 1122, margin: '0 auto', padding: '20px 28px',
      fontFamily: '"Google Sans", Arial, sans-serif',
      minHeight: '100vh', boxSizing: 'border-box'
    }}>

      {/* Header bar */}
      <div style={{ display: 'flex', justifyContent: 'space-between',
        alignItems: 'center', marginBottom: 18 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 20, fontWeight: 500, color: '#202124' }}>
            North Star Metrics
          </h2>
          <div style={{ fontSize: 12, color: '#80868b', marginTop: 3 }}>
            {data.org.name} · {data.org.head} · Updated {lastUpdated.toLocaleTimeString('en-GB')}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {counts.at_risk > 0 && (
            <span style={{ background: '#fef7e0', color: '#b06000', fontSize: 12,
              fontWeight: 700, padding: '5px 12px', borderRadius: 20 }}>
              ⚠ {counts.at_risk} at risk
            </span>
          )}
          {counts.behind > 0 && (
            <span style={{ background: '#fce8e6', color: '#c5221f', fontSize: 12,
              fontWeight: 700, padding: '5px 12px', borderRadius: 20 }}>
              ✕ {counts.behind} behind
            </span>
          )}
          {counts.on_track === data.teams.length && (
            <span style={{ background: '#e6f4ea', color: '#137333', fontSize: 12,
              fontWeight: 700, padding: '5px 12px', borderRadius: 20 }}>
              ✓ All on track
            </span>
          )}
        </div>
      </div>

      {/* Org North Star */}
      <OrgPanel org={data.org} />

      {/* Filter tabs */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 14, alignItems: 'center' }}>
        <span style={{ fontSize: 11, color: '#80868b', fontWeight: 600,
          letterSpacing: 0.5, textTransform: 'uppercase', marginRight: 6 }}>Teams:</span>
        {[
          { id: 'all',      label: `All (${data.teams.length})` },
          { id: 'on_track', label: `On track (${counts.on_track})` },
          { id: 'at_risk',  label: `At risk (${counts.at_risk})` },
          { id: 'behind',   label: `Behind (${counts.behind})` },
        ].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)} style={{
            padding: '5px 14px', border: 'none', borderRadius: 20, fontSize: 12,
            cursor: 'pointer', fontFamily: 'inherit',
            background: filter === f.id ? '#1a73e8' : '#f1f3f4',
            color: filter === f.id ? '#fff' : '#5f6368',
            fontWeight: filter === f.id ? 600 : 400,
          }}>
            {f.label}
          </button>
        ))}
      </div>

      {/* 2-column grid of team cards */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: 12,
        alignItems: 'start'
      }}>
        {filtered.map((team, i) => (
          <div key={team.id} style={filtered.length % 2 !== 0 && i === filtered.length - 1 ? { gridColumn: '1 / -1' } : {}}>
          <TeamCard
            team={team}
            expanded={expanded === team.id}
            onToggle={() => setExpanded(expanded === team.id ? null : team.id)}
          />
          </div>
        ))}
      </div>

      <div style={{ marginTop: 20, fontSize: 11, color: '#bdc1c6', textAlign: 'right' }}>
        EA System · {data.org.name} · Auto-refresh every 60s
      </div>
    </div>
  );
};

export default NorthStarPanel;

